import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { authClient } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { getDeviceId } from "@/utils/deviceId";
import { hardRefresh } from "@/utils/hardRefresh";
import { takePostLoginTarget } from "@/utils/postLoginRedirect";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { ClearSessionButton } from "@/components/ClearSessionButton";
import type { AuthUser } from "@/store/auth";

const schema = z.object({
  username: z.string().min(1, "Podaj nazwę użytkownika"),
  password: z.string().min(1, "Podaj hasło"),
});
type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [serverError, setServerError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      authClient.post<{
        data: {
          user?: AuthUser;
          access?: string;
          refresh?: string;
          username?: string;
        };
        meta: { code?: string };
      }>("/auth/login/", { ...data, device_id: getDeviceId() }),
    onSuccess: async (response) => {
      const { data: respData, meta } = response.data;
      if (meta?.code === "sms_verification_required") {
        sessionStorage.setItem("pending_username", respData?.username ?? "");
        sessionStorage.setItem(
          "pending_masked_phone",
          (respData as any)?.masked_phone ?? "",
        );
        navigate("/auth/sms-verify");
      } else if (respData?.user && respData.access && respData.refresh) {
        setAuth(respData.user, respData.access, respData.refresh);
        // Resume where the user was headed (e.g. a GG notification's conversation)
        // if the login was an interruption rather than a fresh start.
        navigate(
          takePostLoginTarget() ??
            (respData.user.role === "contractor" ? "/contractor/orders" : "/"),
        );
        await hardRefresh();
      }
    },
    onError: (err: any) => {
      setServerError(
        err.response?.data?.errors?.non_field_errors?.[0] ?? "Błąd logowania",
      );
    },
  });

  return (
    <div className="w-full max-w-sm">
      <div className="card p-6 sm:p-8">
        <div className="mb-8">
          <div>
            <span className="font-mono text-3xl font-bold text-[var(--color-primary)] uppercase tracking-tight">
              ELGW{" "}
            </span>
            <span className="font-mono text-3xl font-bold text-[var(--color-text-primary)] uppercase tracking-tight">
              5
            </span>
          </div>
          <p className="section-label mt-1">Portal GK ELEKTIM</p>
        </div>
        <form
          onSubmit={handleSubmit((d) => mutation.mutate(d))}
          className="flex flex-col gap-4"
        >
          <Input
            label="Nazwa użytkownika"
            error={errors.username?.message}
            {...register("username")}
            autoComplete="username"
          />
          <Input
            label="Hasło"
            type="password"
            error={errors.password?.message}
            {...register("password")}
            autoComplete="current-password"
          />
          {serverError && (
            <p className="text-xs font-mono text-[var(--color-primary)]">
              {serverError}
            </p>
          )}
          <Button
            type="submit"
            loading={mutation.isPending}
            className="mt-2 w-full"
          >
            Zaloguj się
          </Button>
        </form>
      </div>
      {/* Always-available reset: clears any leftover/stale local session data. */}
      <div className="mt-4 flex justify-center">
        <ClearSessionButton />
      </div>
    </div>
  );
}
